import Vue from 'vue'
import axios from 'axios'
import { auth } from 'utils'
import { has, forIn, each, includes } from 'lodash'
import router from '@/router'

axios.defaults.baseURL = process.env.VUE_APP_API_URL
axios.defaults.withCredentials = true

let showingUnauthorizedError = false

/**
 * Create JWT request interceptor so we are always sending a JWT with outgoing
 * requests to the API.
 */
axios.interceptors.request.use((config) => {
  if (config.disableAuthorization === undefined || config.disableAuthorization === false) {
    config.headers.common['Authorization'] = `Bearer ${auth.getToken()}`
  }

  return config
})

/**
 * Create the response interceptor for handling JWT refresh tokens. This applies
 * to both successful responses and error responses.
 */
const updateTokenFromHeaders = (headers) => {
  if (has(headers, 'authorization')) {
    auth.updateTokenFromHeader(headers.authorization)
  }
}

axios.interceptors.response.use((response) => {
  updateTokenFromHeaders(response.headers)

  return response
}, async (error) => {
  if (error.response && error.response.status === 401) {
    let route = null

    if (includes(['login', 'dashboard'], router.currentRoute.name)) {
      route = router.currentRoute
    }

    await auth.redirectOnTokenExpired(route)

    return Promise.reject(error)
  }

  // For any other error status code we want to update the token from the response headers.
  updateTokenFromHeaders(error.response.headers)

  // FD#12180 - Custom Error Messages
  // No Manager
  if (error.response && error.response.status === 403 && error.response.data.message === 'No Manager') {
    Vue.noty.error('You do not have an assigned manager, please contact your Agency Coordinator.').on('afterClose', () => {
      showingUnauthorizedError = false
    })

    showingUnauthorizedError = true

    auth.redirectOnForbidden()

    return Promise.reject(new Error('Forbidden'))
  }

  // No Agency
  if (error.response && error.response.status === 403 && error.response.data.message === 'No Agency') {
    Vue.noty.error('You do not have an assigned agency, please contact the Graduate Program Administrator.').on('afterClose', () => {
      showingUnauthorizedError = false
    })

    showingUnauthorizedError = true

    auth.redirectOnForbidden()

    return Promise.reject(new Error('Forbidden'))
  }

  // Generic No Permission
  if (error.response && error.response.status === 403 && !showingUnauthorizedError) {
    Vue.noty.error('You do not have permission to do that.').on('afterClose', () => {
      showingUnauthorizedError = false
    })

    showingUnauthorizedError = true
  }

  // Generic 404 Permission
  if (error.response && error.response.status === 404) {
    Vue.noty.error('The page you have requested is not found.')

    auth.redirectOnForbidden()
  }

  // 500 Server Errors
  if (error.response && (error.response.status === 500 || error.response.status === 400)) {
    Vue.noty.error(error.response.data.message)
  }

  return Promise.reject(error)
})

// Set Axios on our Vue instances so we can have easier access to it via "this.$http" should we
// need to make requests within our components.
Vue.prototype.$http = axios

export default axios

/**
 * Handle server validation errors by adding them to an error bag.
 *
 * @return void
 */
export const handleServerErrors = (exception, validator, scope) => {
  validator.errors.clear()

  if (exception.response && exception.response.status === 422) {
    forIn(exception.response.data.errors, (errors, name) => {
      let field = validator.fields.find({ name })

      if (!field) {
        return
      }

      field.setFlags({ invalid: true, valid: false, validated: true })

      each(errors, (msg) => {
        validator.errors.add({
          field: field.name,
          id: field.id,
          msg
        })
      })
    })
  } else {
    throw exception
  }
}

/**
 * Generate URL to API.
 *
 * @return string
 */
export const apiUrl = (path) => {
  let base = process.env.VUE_APP_API_URL

  if (path === undefined) {
    return base
  }

  if (base.endsWith('/')) {
    base = base.slice(0, -1) // remove extra slash
  }

  return `${base}/${path}?token=${auth.getToken()}`
}
